/**
 * EIR Connections — an outreach tracker for people an EIR meets informally
 * (events, happy hours, intros) who aren't yet on the portal.
 *
 * Mirrors the founder Investor CRM (InvestorCRM.js): a searchable/filterable
 * table with inline status + follow-up editing and a slide-panel detail with a
 * notes thread. Adds a funnel status (met → … → onboarded) and a "Send invite"
 * action that emails the person a link to apply and advances them to "invited".
 *
 * Each EIR sees only their own connections (enforced server-side).
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  listConnections, createConnection, updateConnection, deleteConnection,
  getConnectionNotes, addConnectionNote, inviteConnection,
} from '../services/apiService';
import SlidePanel from '../components/SlidePanel';

const STATUSES = [
  { value: 'met',          label: 'Met',           color: '#6b7280', bg: '#f3f4f6' },
  { value: 'in_touch',     label: 'In Touch',      color: '#2563eb', bg: '#eff6ff' },
  { value: 'invited',      label: 'Invited',       color: '#d97706', bg: '#fef3c7' },
  { value: 'applied',      label: 'Applied',       color: '#7c3aed', bg: '#f5f3ff' },
  { value: 'onboarded',    label: 'Onboarded',     color: '#15803d', bg: '#dcfce7' },
  { value: 'not_pursuing', label: 'Not Pursuing',  color: '#dc2626', bg: '#fee2e2' },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

// Parse a YYYY-MM-DD string as a local date (avoids the UTC shift that
// new Date('YYYY-MM-DD') would introduce).
function parseLocalDate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Red if the follow-up is overdue, amber if within 3 days, null otherwise.
function followUpColor(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((parseLocalDate(dateStr) - today) / 86400000);
  if (diff < 0)  return '#dc2626';
  if (diff <= 3) return '#d97706';
  return null;
}

/** EIR outreach pipeline table with detail panel + invite action. */
export default function EIRConnections() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);

  useEffect(() => {
    listConnections()
      .then(({ connections: c }) => setConnections(c))
      .finally(() => setLoading(false));
  }, []);

  const handleStatusChange = useCallback(async (id, status) => {
    setConnections((prev) => prev.map((c) => c.id === id ? { ...c, status } : c));
    await updateConnection(id, { status });
  }, []);

  const handleDateChange = useCallback(async (id, value) => {
    setConnections((prev) => prev.map((c) => c.id === id ? { ...c, next_follow_up_date: value || null } : c));
    await updateConnection(id, { next_follow_up_date: value || null });
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this connection?')) return;
    await deleteConnection(id);
    setConnections((prev) => prev.filter((c) => c.id !== id));
    if (selected?.id === id) setPanelOpen(false);
  }, [selected]);

  const handleCreate = useCallback(async (data) => {
    const created = await createConnection(data);
    setConnections((prev) => [created, ...prev]);
    setShowAddRow(false);
  }, []);

  const handleUpdate = useCallback(async (id, data) => {
    const updated = await updateConnection(id, data);
    setConnections((prev) => prev.map((c) => c.id === id ? { ...c, ...updated } : c));
    setSelected((prev) => prev?.id === id ? { ...prev, ...updated } : prev);
  }, []);

  const openPanel = useCallback((conn) => {
    setSelected(conn);
    setPanelOpen(true);
  }, []);

  const filtered = connections.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q) || (c.source || '').toLowerCase().includes(q);
    return matchSearch && (!statusFilter || c.status === statusFilter);
  });

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Connections</h1>
            <p style={styles.subtitle}>People you've met and are working toward the program.</p>
          </div>
        </div>

        <div style={styles.controls}>
          <div style={styles.searchWrap}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={styles.searchIcon}>
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input style={styles.searchInput} placeholder="Search connections…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select style={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={{ ...styles.th, width: 36 }}>#</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Name</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Company</th>
                <th style={{ ...styles.th, width: 44 }}>Email</th>
                <th style={{ ...styles.th, minWidth: 150 }}>Where met</th>
                <th style={{ ...styles.th, minWidth: 140 }}>Status</th>
                <th style={{ ...styles.th, minWidth: 152 }}>Next follow-up</th>
                <th style={{ ...styles.th, width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={styles.emptyCell}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && !showAddRow && (
                <tr><td colSpan={8} style={styles.emptyCell}>No connections yet. Click "+ Add Connection" below.</td></tr>
              )}
              {filtered.map((c, i) => (
                <ConnectionRow key={c.id} conn={c} rowNum={i + 1}
                  onStatusChange={handleStatusChange}
                  onDateChange={handleDateChange}
                  onDelete={handleDelete}
                  onClick={() => openPanel(c)}
                />
              ))}
              {showAddRow && <AddConnectionRow onSave={handleCreate} onCancel={() => setShowAddRow(false)} />}
            </tbody>
          </table>
        </div>

        <button style={styles.addBtn} onClick={() => setShowAddRow(true)}>+ Add Connection</button>

      </div>

      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={selected?.name || ''}>
        {selected && (
          <ConnectionDetail
            conn={selected}
            onUpdate={handleUpdate}
            onDelete={(id) => { handleDelete(id); setPanelOpen(false); }}
          />
        )}
      </SlidePanel>
    </div>
  );
}

// One connection row. Status select, follow-up date, and delete each
// stopPropagation so they don't trigger the row's open-detail onClick.
function ConnectionRow({ conn, rowNum, onStatusChange, onDateChange, onDelete, onClick }) {
  const status = STATUS_MAP[conn.status] || STATUS_MAP.met;
  const followUp = followUpColor(conn.next_follow_up_date);
  return (
    <tr style={styles.tr} onClick={onClick}>
      <td style={styles.numCell}>{rowNum}</td>
      <td style={{ ...styles.td, fontWeight: 700 }}>{conn.name}</td>
      <td style={styles.td}>{conn.company || <span style={styles.dim}>—</span>}</td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        {conn.email
          ? <a href={`mailto:${conn.email}`} style={styles.emailBtn} title={conn.email}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>
            </a>
          : <span style={styles.dim}>—</span>}
      </td>
      <td style={styles.td}>{conn.source || <span style={styles.dim}>—</span>}</td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        <select value={conn.status} onChange={(e) => onStatusChange(conn.id, e.target.value)}
          style={{ ...styles.statusSelect, background: status.bg, color: status.color }}>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        <input type="date" value={conn.next_follow_up_date || ''}
          style={{ ...styles.dateInput, ...(followUp ? { color: followUp, fontWeight: 800 } : {}) }}
          onChange={(e) => onDateChange(conn.id, e.target.value)} />
      </td>
      <td style={styles.td} onClick={(e) => e.stopPropagation()}>
        <button style={styles.deleteRowBtn} onClick={() => onDelete(conn.id)} title="Delete">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
            <path d="M10 11v6M14 11v6M9 6V4h6v2" />
          </svg>
        </button>
      </td>
    </tr>
  );
}

function AddConnectionRow({ onSave, onCancel }) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('met');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    try { await onSave({ name: name.trim(), company: company || null, email: email || null, source: source || null, status }); }
    finally { setSaving(false); }
  }

  return (
    <tr style={{ ...styles.tr, background: '#f9fdfb' }}>
      <td style={styles.numCell}>+</td>
      <td style={styles.td}><input autoFocus style={styles.inlineInput} placeholder="Name *" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} /></td>
      <td style={styles.td}><input style={styles.inlineInput} placeholder="Company" value={company} onChange={(e) => setCompany(e.target.value)} /></td>
      <td style={styles.td}><input style={styles.inlineInput} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} /></td>
      <td style={styles.td}><input style={styles.inlineInput} placeholder="Where you met" value={source} onChange={(e) => setSource(e.target.value)} /></td>
      <td style={styles.td}>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          style={{ ...styles.statusSelect, background: STATUS_MAP[status]?.bg, color: STATUS_MAP[status]?.color }}>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </td>
      <td colSpan={2} style={styles.td}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={styles.saveRowBtn} onClick={save} disabled={saving || !name.trim()}>{saving ? '…' : 'Save'}</button>
          <button style={styles.cancelRowBtn} onClick={onCancel}>Cancel</button>
        </div>
      </td>
    </tr>
  );
}

// Slide-panel editor for a single connection: a local form draft saved via
// onUpdate, an invite action, and a notes thread.
function ConnectionDetail({ conn, onUpdate, onDelete }) {
  const [form, setForm] = useState({ ...conn });
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const status = STATUS_MAP[form.status] || STATUS_MAP.met;

  useEffect(() => { getConnectionNotes(conn.id).then(setNotes); }, [conn.id]);

  async function save() {
    setSaving(true);
    try { await onUpdate(conn.id, form); } finally { setSaving(false); }
  }

  async function invite() {
    if (!form.email) { alert('Add an email address before sending an invite.'); return; }
    if (!window.confirm(`Email ${form.name} an invite to apply?`)) return;
    setInviting(true);
    try {
      const updated = await inviteConnection(conn.id);
      await onUpdate(conn.id, { status: updated.status });
      setForm((f) => ({ ...f, status: updated.status, invited_at: updated.invited_at }));
    } finally { setInviting(false); }
  }

  async function submitNote() {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const note = await addConnectionNote(conn.id, noteText.trim());
      setNotes((prev) => [...prev, note]);
      setNoteText('');
    } finally { setSavingNote(false); }
  }

  function Field({ label, k, type = 'text' }) {
    return (
      <div style={panelStyles.field}>
        <label style={panelStyles.label}>{label}</label>
        <input style={panelStyles.input} type={type} value={form[k] || ''}
          onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
      </div>
    );
  }

  return (
    <div style={panelStyles.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ ...panelStyles.statusBadge, background: status.bg, color: status.color }}>{status.label}</span>
        <button style={panelStyles.deleteBtn} onClick={() => onDelete(conn.id)}>Delete</button>
      </div>

      <div style={panelStyles.grid}>
        <Field label="Name" k="name" />
        <Field label="Company" k="company" />
        <Field label="Email" k="email" type="email" />
        <Field label="Phone" k="phone" type="tel" />
        <Field label="Title" k="title" />
        <Field label="Where met" k="source" />
        <div style={panelStyles.field}>
          <label style={panelStyles.label}>Status</label>
          <select style={{ ...panelStyles.input, background: status.bg, color: status.color, fontWeight: 800 }}
            value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <Field label="Next Follow-up" k="next_follow_up_date" type="date" />
      </div>

      <div style={panelStyles.field}>
        <label style={panelStyles.label}>Next Step</label>
        <textarea style={{ ...panelStyles.input, resize: 'vertical', minHeight: 46 }} rows={2}
          value={form.next_step || ''} onChange={(e) => setForm((f) => ({ ...f, next_step: e.target.value }))} />
      </div>

      <button style={panelStyles.saveBtn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>

      {/* Invite-to-apply email is disabled for now — EIRs direct prospects to the
          application page manually. Re-enable by uncommenting; the invite()
          handler, inviting state, and POST /connections/{id}/invite endpoint
          all remain in place.
      <div style={panelStyles.inviteRow}>
        <button style={panelStyles.inviteBtn} onClick={invite} disabled={inviting}>
          {inviting ? 'Sending…' : 'Send invite to apply'}
        </button>
        {form.invited_at && (
          <span style={panelStyles.invitedNote}>
            Invited {new Date(form.invited_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>
      */}

      <div style={panelStyles.divider} />

      <p style={panelStyles.notesTitle}>
        Notes {notes.length > 0 && <span style={panelStyles.noteCount}>{notes.length}</span>}
      </p>
      <div style={panelStyles.noteInput}>
        <textarea style={panelStyles.textarea} rows={3} placeholder="Add a note…"
          value={noteText} onChange={(e) => setNoteText(e.target.value)} />
        <button style={panelStyles.addNoteBtn} onClick={submitNote} disabled={savingNote || !noteText.trim()}>
          {savingNote ? '…' : 'Add'}
        </button>
      </div>
      <div style={panelStyles.notesList}>
        {notes.length === 0 && <p style={panelStyles.dimText}>No notes yet.</p>}
        {[...notes].reverse().map((n) => (
          <div key={n.id} style={panelStyles.noteItem}>
            <p style={panelStyles.noteBody}>{n.body}</p>
            <p style={panelStyles.noteDate}>{n.created_at ? new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: '98%', margin: '0 auto' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 'var(--space-5)', paddingTop: 'var(--space-2)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 'var(--font-size-page-title)', fontWeight: 'var(--font-weight-page-title)', color: 'var(--color-rust)', lineHeight: 1.1, marginBottom: 4 },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-page-subtitle)', color: 'var(--color-forest)', opacity: 0.7 },
  controls: { display: 'flex', gap: 12, marginBottom: 'var(--space-3)', flexWrap: 'wrap' },
  searchWrap: { flex: 1, minWidth: 200, position: 'relative' },
  searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-forest)', opacity: 0.4 },
  searchInput: { width: '100%', padding: '8px 12px 8px 34px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 20, color: 'var(--color-forest)', boxSizing: 'border-box', outline: 'none' },
  filterSelect: { padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none' },
  tableWrap: { background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', overflow: 'auto', marginBottom: 8 },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 700 },
  thead: { background: '#f8fafb' },
  th: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 800, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5', cursor: 'pointer' },
  td: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', padding: '10px 12px', verticalAlign: 'middle' },
  numCell: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.35, padding: '10px 12px', textAlign: 'center', verticalAlign: 'middle' },
  emptyCell: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.45, padding: '28px 16px', textAlign: 'center', fontStyle: 'italic' },
  dim: { opacity: 0.35 },
  statusSelect: { padding: '3px 8px', borderRadius: 'var(--radius-full)', border: 'none', fontSize: 15, fontWeight: 800, fontFamily: 'var(--font-body)', cursor: 'pointer', outline: 'none' },
  dateInput: { border: 'none', background: 'transparent', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none', cursor: 'pointer', width: 142, minWidth: 142, boxSizing: 'border-box' },
  emailBtn: { color: 'var(--color-forest)', opacity: 0.6, display: 'flex', alignItems: 'center' },
  deleteRowBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', opacity: 0.6, padding: 2, display: 'flex', alignItems: 'center' },
  inlineInput: { border: 'none', borderBottom: '1px solid var(--color-mint-dark)', background: 'transparent', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none', width: '100%', padding: '2px 0' },
  saveRowBtn: { padding: '4px 14px', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 800, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer' },
  cancelRowBtn: { padding: '4px 10px', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1px solid var(--color-mint-dark)', cursor: 'pointer' },
  addBtn: { background: 'none', border: 'none', fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.6, cursor: 'pointer', padding: '6px 0' },
};

const panelStyles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  statusBadge: { padding: '3px 12px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 800 },
  deleteBtn: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', padding: '4px 12px', cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 800, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '7px 10px', borderRadius: 'var(--radius-sm)', border: '1.5px solid #e0e0e0', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none', width: '100%', boxSizing: 'border-box' },
  saveBtn: { padding: '9px', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 800, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer', width: '100%' },
  inviteRow: { display: 'flex', alignItems: 'center', gap: 12 },
  inviteBtn: { padding: '9px 16px', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 800, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', cursor: 'pointer' },
  invitedNote: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.55 },
  divider: { height: 1, background: '#f0f0f0', margin: '4px 0' },
  notesTitle: { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: 'var(--color-forest)', display: 'flex', alignItems: 'center', gap: 8 },
  noteCount: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 800, background: 'var(--color-mint-dark)', color: 'var(--color-forest)', padding: '1px 7px', borderRadius: 'var(--radius-full)' },
  noteInput: { display: 'flex', gap: 8, alignItems: 'flex-start' },
  textarea: { flex: 1, padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1.5px solid #e0e0e0', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', outline: 'none', resize: 'none', lineHeight: 1.5 },
  addNoteBtn: { padding: '8px 14px', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 800, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', border: 'none', cursor: 'pointer', flexShrink: 0 },
  notesList: { display: 'flex', flexDirection: 'column', gap: 8 },
  noteItem: { background: '#f8fafb', borderRadius: 'var(--radius-sm)', padding: '10px 12px', borderLeft: '3px solid var(--color-mint-dark)' },
  noteBody: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  noteDate: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.45, marginTop: 4 },
  dimText: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.4, fontStyle: 'italic' },
};
